import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { createEnumDropdown } from "@freightsimple/ui-core/Controls/EnumDropdown";
import { DateRangePicker } from "@freightsimple/ui-core/DateTime/Controls/DateRangePicker";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { T } from "@freightsimple/ui-core/Typography/T";
import { CompanyWebhookEventType } from "@freightsimple/generated-dashboard-openapi-client/models/CompanyWebhookEventType";
import { WebhookDeliveryState } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliveryState";
import type { WebhookSummary } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookSummary";
import type { LocalDate } from "@freightsimple/time/branded-types";
import {
  describeWebhookDeliveryState,
  describeWebhookEventType,
  webhookDeliveryStateVariant,
} from "../Helpers/webhooksLabels";
import {
  WEBHOOKS_FILTER_ANY,
  type WebhooksDeliveriesFilter,
} from "../Hooks/useWebhooksDeliveriesFilters";

const EventTypeDropdown = createEnumDropdown(
  CompanyWebhookEventType,
  describeWebhookEventType,
  { sorted: true },
);

const StateDropdown = createEnumDropdown(
  WebhookDeliveryState,
  describeWebhookDeliveryState,
  { colorForItem: webhookDeliveryStateVariant },
);

interface WebhooksDeliveriesFilterBarProps {
  webhooks: WebhookSummary[];
  filter: WebhooksDeliveriesFilter;
  setWebhookId: (webhookId: string) => void;
  setEventType: (
    eventType: CompanyWebhookEventType | typeof WEBHOOKS_FILTER_ANY,
  ) => void;
  setState: (state: WebhookDeliveryState | typeof WEBHOOKS_FILTER_ANY) => void;
  setDateRange: (range: [LocalDate, LocalDate | undefined] | undefined) => void;
}

/**
 * The four ways to narrow a delivery list that can run to thousands of rows: which endpoint,
 * which event, how it ended, and when. All of them are applied on the server, so the table only
 * ever holds one page.
 */
export function WebhooksDeliveriesFilterBar({
  webhooks,
  filter,
  setWebhookId,
  setEventType,
  setState,
  setDateRange,
}: WebhooksDeliveriesFilterBarProps) {
  const webhookOptions = [
    { value: WEBHOOKS_FILTER_ANY, label: "Any endpoint" },
    ...webhooks.map((webhook) => ({
      value: webhook.companyWebhookId,
      label: webhook.description
        ? `${webhook.description} (${webhook.webhookUrl})`
        : webhook.webhookUrl,
    })),
  ];

  return (
    // Labels beside the controls rather than above: this is a filter bar, not a form. Boxed so
    // the run of controls reads as controls, the same way the saved commodities bar does.
    <ControlPresentationProvider presentation="boxed">
      <Row gap={4} align="center" wrap>
        <Row gap={2} align="center">
          <T size="sm" nowrap>
            Endpoint
          </T>
          <Dropdown
            options={webhookOptions}
            value={filter.webhookId}
            onChange={setWebhookId}
            // Dropdown drops an option whose value is "", so "Any endpoint" only shows as
            // the placeholder and can't be picked again: the X is the way back to it.
            onClear={() => setWebhookId(WEBHOOKS_FILTER_ANY)}
            width="lg"
            data-testid="webhook-deliveries-endpoint-filter"
          />
        </Row>
        <Row gap={2} align="center">
          <T size="sm" nowrap>
            Event
          </T>
          <EventTypeDropdown
            value={
              filter.eventType === WEBHOOKS_FILTER_ANY
                ? undefined
                : filter.eventType
            }
            onChange={(value) => setEventType(value ?? WEBHOOKS_FILTER_ANY)}
            placeholder="Any event"
            data-testid="webhook-deliveries-event-filter"
          />
        </Row>
        <Row gap={2} align="center">
          <T size="sm" nowrap>
            Outcome
          </T>
          <StateDropdown
            value={
              filter.state === WEBHOOKS_FILTER_ANY ? undefined : filter.state
            }
            onChange={(value) => setState(value ?? WEBHOOKS_FILTER_ANY)}
            placeholder="Any outcome"
            data-testid="webhook-deliveries-outcome-filter"
          />
        </Row>
        <Row gap={2} align="center">
          <T size="sm" nowrap>
            Sent
          </T>
          <DateRangePicker
            allowOpenEnded
            allowClear
            placeholder={["From", "To"]}
            value={[filter.from, filter.to]}
            onChange={setDateRange}
          />
        </Row>
      </Row>
    </ControlPresentationProvider>
  );
}
